import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

export const MobileNav: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [showHint, setShowHint] = useState(false)
  const location = useLocation()

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false)
  }, [location])

  // First-time user hint animation
  useEffect(() => {
    const hasSeenHint = localStorage.getItem('navHintSeen')
    if (!hasSeenHint) {
      setTimeout(() => {
        setShowHint(true)
        setTimeout(() => {
          setShowHint(false)
          localStorage.setItem('navHintSeen', 'true')
        }, 3000)
      }, 1000)
    }
  }, [])

  const toggleMenu = () => {
    setIsOpen(!isOpen)
  }

  return (
    <>
      {/* Dynamic Island Style Navigation */}
      <div 
        className="fixed top-0 left-0 right-0 z-50 pointer-events-none"
        style={{ 
          paddingTop: 'calc(20px + env(safe-area-inset-top))',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <motion.button
          onClick={toggleMenu}
          className="backdrop-blur-xl rounded-full shadow-lg pointer-events-auto"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '10px 20px',
            backgroundColor: 'rgba(247, 245, 245, 0.95)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
            minHeight: '44px', // Meets 44x44px touch target requirement
            minWidth: '44px',
            maxWidth: 'fit-content'
          }}
          initial={{ y: -120, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ 
            duration: 0.4, 
            ease: [0.25, 0.46, 0.45, 0.94],
            delay: 0
          }}
          whileTap={{ scale: 0.95 }}
        >
          <img
            src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
            alt="Mernaki Logo"
            style={{
              width: '28px',
              height: '28px',
              objectFit: 'contain',
              flexShrink: 0
            }}
          />
          <h1 
            style={{ 
              color: '#1A1A1A',
              fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
              fontWeight: 400,
              fontSize: '18px',
              margin: 0,
              lineHeight: 1,
              whiteSpace: 'nowrap'
            }}
          >
            Mernaki
          </h1>
        </motion.button>
      </div>

      {/* First-time hint */}
      <AnimatePresence>
        {showHint && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="backdrop-blur-md rounded-full"
            style={{
              position: 'fixed',
              top: 'calc(92px + env(safe-area-inset-top))',
              left: '50%',
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
              fontSize: '12px',
              color: '#666',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 500,
              zIndex: 50,
              padding: '6px 16px',
              backgroundColor: 'rgba(247, 245, 245, 0.9)',
              border: '1px solid rgba(0, 0, 0, 0.08)'
            }}
          >
            Tap to Navigate
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Bubble Menu Items */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Blurred Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                zIndex: 40
              }}
              onClick={toggleMenu}
            />

            {/* Individual Floating Bubbles */}
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 45, pointerEvents: 'none' }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: '8px',
                paddingTop: 'calc(88px + env(safe-area-inset-top))'
              }}>
                <MenuBubble to="/" label="Home" delay={0} />
                <MenuBubble to="/products" label="Products" delay={0.05} />
                <MenuBubble to="/#services" label="Services" delay={0.1} />
                <MenuBubble to="/booking" label="Booking" delay={0.15} />
                <MenuBubble to="/about" label="About" delay={0.2} />
                <MenuBubble to="/contact" label="Contact" delay={0.25} />
              </div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

const MenuBubble: React.FC<{ to: string; label: string; delay: number }> = ({ to, label, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.8 }}
      transition={{ 
        duration: 0.3, 
        ease: [0.4, 0.0, 0.2, 1],
        delay 
      }}
      style={{ pointerEvents: 'auto' }}
    >
      <Link
        to={to}
        className="backdrop-blur-xl rounded-full shadow-lg"
        style={{
          fontSize: 'clamp(14px, 3vw, 15px)',
          fontWeight: 500,
          color: '#1A1A1A',
          textDecoration: 'none',
          padding: 'clamp(10px, 2vw, 12px) clamp(24px, 5vw, 28px)',
          minHeight: '44px', // Meets 44x44px touch target requirement
          minWidth: '44px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(247, 245, 245, 0.95)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
          WebkitTapHighlightColor: 'transparent',
          transition: 'all 0.2s ease',
          whiteSpace: 'nowrap'
        }}
        onTouchStart={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(247, 245, 245, 1)'
          e.currentTarget.style.transform = 'scale(0.95)'
        }}
        onTouchEnd={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(247, 245, 245, 0.95)'
          e.currentTarget.style.transform = 'scale(1)'
        }}
      >
        {label}
      </Link>
    </motion.div>
  )
}
